import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, MapPin, Map, FileImage } from 'lucide-react';
import { useFirebaseImage, ImageSize, getImagePathWithSize } from '../Helpers/ImageLoader';
import { BaseComponent, Building } from '../types/protocols';
import { componentLocations, LocationData } from '../Helpers/componentLocations';
import LocationPin from './LocationPin';
import './LocationDisplay.css';

// LocationData and componentLocations live in Helpers/componentLocations (pure, no React)
export type { LocationType, LocationData } from '../Helpers/componentLocations';
export { componentLocations };

/**
 * Props for the LocationThumbnail component.
 */
interface LocationThumbnailProps {
    /** Location data to display */
    location: LocationData;
    /** Click handler - typically opens the modal; gets the event so a caller can stop it bubbling */
    onClick: (e: React.MouseEvent<HTMLDivElement>) => void;
}

/**
 * Small thumbnail showing a location with a positioned marker.
 * Accounts for object-fit: cover cropping when positioning the marker.
 */
export const LocationThumbnail: React.FC<LocationThumbnailProps> = ({ location, onClick }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
    const thumbnailPath = getImagePathWithSize(location.storagePath, ImageSize.THUMBNAIL_SMALL);
    const { src, isLoading, hasError } = useFirebaseImage(thumbnailPath);

    const IconComponent = location.type === 'photo' ? MapPin :
                         location.type === 'map' ? Map : FileImage;

    // Track container size for marker positioning
    useEffect(() => {
        const updateSize = () => {
            if (containerRef.current) {
                setContainerSize({
                    width: containerRef.current.offsetWidth,
                    height: containerRef.current.offsetHeight,
                });
            }
        };

        const resizeObserver = new ResizeObserver(updateSize);
        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }
        updateSize();

        return () => resizeObserver.disconnect();
    }, []);

    // Calculate marker position accounting for object-fit: cover
    const markerStyle = useMemo(() => {
        if (!containerSize.width || !containerSize.height || !location.width || !location.height) {
            return null;
        }

        const containerW = containerSize.width;
        const containerH = containerSize.height;
        const imageW = location.width;
        const imageH = location.height;

        // object-fit: cover scales to fill container, cropping overflow
        const scale = Math.max(containerW / imageW, containerH / imageH);
        const renderedW = imageW * scale;
        const renderedH = imageH * scale;

        // Offset where the image starts (centered)
        const offsetX = (containerW - renderedW) / 2;
        const offsetY = (containerH - renderedH) / 2;

        // Marker position in container coordinates
        const markerX = offsetX + (location.x * scale);
        const markerY = offsetY + (location.y * scale);

        return {
            left: `${markerX}px`,
            top: `${markerY}px`,
        };
    }, [containerSize, location]);

    const shortLabel = location.type === 'photo' ? 'Foto' :
                       location.type === 'map' ? 'Karta' :
                       location.levelName || 'Ritning';

    return (
        <div
            ref={containerRef}
            className="location-display-thumbnail"
            onClick={onClick}
            title={location.label}
        >
            {isLoading ? (
                <div className="location-display-thumbnail-loading">
                    <Loader2 size={16} className="location-display-spinner" />
                </div>
            ) : hasError || !src ? (
                <div className="location-display-thumbnail-error">
                    <IconComponent size={16} />
                </div>
            ) : (
                <>
                    <img src={src} alt={location.label} />
                    {markerStyle && (
                        <div
                            className="location-display-marker"
                            style={markerStyle}
                        />
                    )}
                </>
            )}
            <div className="location-display-label">
                <IconComponent size={10} />
                <span>{shortLabel}</span>
            </div>
        </div>
    );
};

/**
 * Props for the LocationModal component.
 */
interface LocationModalProps {
    /** Location data to display */
    location: LocationData;
    /** Handler called when the modal should close */
    onClose: () => void;
}

const COMPASS_POINTS = [
    { letter: 'N', side: 'n', rotation: 0 },
    { letter: 'Ö', side: 'e', rotation: 90 },
    { letter: 'S', side: 's', rotation: 180 },
    { letter: 'V', side: 'w', rotation: 270 },
] as const;

/**
 * N/Ö/S/V inside the edges of a satellite image, each in a white arrowhead
 * (notched back) pointing out at its edge. The arrowhead is drawn pointing up
 * and rotated; the letter is turned back so it stays upright. Satellite
 * images are north-up.
 */
const CompassPoints: React.FC = () => (
    <div className="location-display-compass" aria-hidden="true">
        {COMPASS_POINTS.map(({ letter, side, rotation }) => (
            <svg
                key={side}
                className={`location-display-compass-point location-display-compass-point--${side}`}
                viewBox="0 0 48 48"
                width="52"
                height="52"
            >
                <g transform={`rotate(${rotation} 24 24)`}>
                    <path d="M24 2L46 46L24 37L2 46Z" fill="white" stroke="white" strokeWidth="2" strokeLinejoin="round" />
                    <text
                        x="24"
                        y="29"
                        transform={`rotate(${-rotation} 24 29)`}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontSize="14"
                        fontWeight="700"
                        fill="#000"
                    >
                        {letter}
                    </text>
                </g>
            </svg>
        ))}
    </div>
);

/**
 * The full-screen shell behind LocationModal and LocationOverlay: dark backdrop,
 * title, close button, ESC to close and body scroll locked while open. A click
 * on the backdrop closes it; children stop propagation where a click means
 * something else.
 */
export const LocationOverlayShell: React.FC<{
    label: string;
    onClose: () => void;
    children: React.ReactNode;
}> = ({ label, onClose, children }) => {
    // ESC key to close
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    // Lock body scroll while overlay is open
    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, []);

    return createPortal(
        <div
            className="location-display-modal-overlay"
            onClick={onClose}
        >
            <button
                className="location-display-modal-close"
                onClick={onClose}
                title="Stäng"
            >
                ✕
            </button>

            <div className="location-display-modal-title" onClick={(e) => e.stopPropagation()}>
                {label}
            </div>

            <div className="location-display-modal-content">
                {children}
            </div>
        </div>,
        document.body
    );
};

/**
 * Full-screen modal overlay showing a location image with positioned marker.
 * Closes on ESC key or clicking outside the image.
 */
export const LocationModal: React.FC<LocationModalProps> = ({ location, onClose }) => {
    const imgRef = useRef<HTMLImageElement>(null);
    const [imgSize, setImgSize] = useState({ width: 0, height: 0 });
    const { src, isLoading, hasError } = useFirebaseImage(location.storagePath);

    const markerPosition = useMemo(() => {
        if (!imgSize.width || !location.width) return null;
        const scale = imgSize.width / location.width;
        return {
            left: `${location.x * scale}px`,
            top: `${location.y * scale}px`,
            width: '18px',
            height: '18px',
        };
    }, [location, imgSize]);

    const handleImageClick = (e: React.MouseEvent) => {
        e.stopPropagation();
    };

    return (
        <LocationOverlayShell label={location.label} onClose={onClose}>
            {isLoading ? (
                <Loader2 size={32} className="location-display-spinner" />
            ) : hasError || !src ? (
                <div className="location-display-modal-error">
                    Kunde inte ladda bild
                </div>
            ) : (
                <div className="location-display-modal-image-wrapper" onClick={handleImageClick}>
                    <img
                        ref={imgRef}
                        src={src}
                        alt={location.label}
                        onLoad={() => {
                            if (imgRef.current) {
                                setImgSize({
                                    width: imgRef.current.clientWidth,
                                    height: imgRef.current.clientHeight,
                                });
                            }
                        }}
                    />
                    {location.type === 'map' && <CompassPoints />}
                    {/* One position is always the red map pin, like the component page */}
                    {markerPosition && <LocationPin left={markerPosition.left} top={markerPosition.top} />}
                </div>
            )}
        </LocationOverlayShell>
    );
};

/**
 * Props for the LocationDisplay component.
 */
interface LocationDisplayProps {
    /** Array of locations to display as thumbnails */
    locations: LocationData[];
    /**
     * Optional click handler for a location thumbnail. When provided, the internal
     * full-screen LocationModal is bypassed and the consumer decides how to present
     * the location (e.g. render a LocationViewer in a side pane).
     */
    onLocationClick?: (location: LocationData) => void;
}

/**
 * Displays a row of location thumbnails with click-to-expand modal functionality.
 *
 * Supports three location types:
 * - **photo**: Position marked in a reference photo (uses MapPin icon)
 * - **map**: Position on a site/building map (uses Map icon)
 * - **blueprint**: Position on a floor plan/blueprint (uses FileImage icon)
 *
 * @example
 * ```tsx
 * const locations: LocationData[] = [
 *   {
 *     type: 'photo',
 *     label: 'Position i foto',
 *     storagePath: 'buildings/123/photos/abc.jpg',
 *     x: 150, y: 200,
 *     width: 1920, height: 1080,
 *   },
 *   {
 *     type: 'blueprint',
 *     label: 'Position på Plan 1',
 *     storagePath: 'buildings/123/blueprints/floor1.jpg',
 *     x: 300, y: 400,
 *     width: 2000, height: 1500,
 *     levelName: 'Plan 1',
 *   },
 * ];
 *
 * <LocationDisplay locations={locations} />
 * ```
 */
const LocationDisplay: React.FC<LocationDisplayProps> = ({ locations, onLocationClick }) => {
    const [selectedLocation, setSelectedLocation] = useState<LocationData | null>(null);

    if (locations.length === 0) {
        return null;
    }

    return (
        <>
            <div className="location-display-thumbnails">
                {locations.map((location, index) => (
                    <LocationThumbnail
                        key={`${location.type}-${location.storagePath}-${index}`}
                        location={location}
                        onClick={() => onLocationClick ? onLocationClick(location) : setSelectedLocation(location)}
                    />
                ))}
            </div>
            {!onLocationClick && selectedLocation && (
                <LocationModal
                    location={selectedLocation}
                    onClose={() => setSelectedLocation(null)}
                />
            )}
        </>
    );
};

export default LocationDisplay;

/** Memoised `componentLocations` for a rendering component. */
export const useComponentLocations = (component: BaseComponent, building: Building): LocationData[] =>
    useMemo(() => componentLocations(component, building), [component, building]);
